import { useState, useEffect, useMemo } from 'react'
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { boxApi } from '../utils/api'

export default function PayoutSchedule({ boxId, refreshKey = 0, isOrganizer = false }) {
  const [schedule, setSchedule] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingDate, setEditingDate] = useState(null)
  const [dateValue, setDateValue] = useState('')
  const [updating, setUpdating] = useState(false)

  const formatMoney = (value) => {
    if (value === null || value === undefined) return '—'
    const num = Number(value)
    if (Number.isNaN(num)) return '—'
    return `$${num.toFixed(2)}`
  }

  useEffect(() => {
    const loadSchedule = async () => {
      try {
        setLoading(true)
        const data = await boxApi.getSchedule(boxId)
        if (Array.isArray(data)) {
          setSchedule(data)
        } else if (data?.error) {
          setError(data.error)
        } else {
          setError('Unable to load schedule')
        }
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    loadSchedule()
  }, [boxId, refreshKey])

  const sortedSchedule = useMemo(() => {
    return [...schedule].sort((a, b) => {
      if ((a.month || 0) === (b.month || 0)) return (a.slot || 0) - (b.slot || 0)
      return (a.month || 0) - (b.month || 0)
    })
  }, [schedule])


  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="text-gray-500">Loading schedule...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md border border-red-200">
        <p className="text-red-800 font-semibold">Error loading schedule: {error}</p>
        {isOrganizer && (
          <p className="text-sm text-red-700 mt-1">
            Fix the schedule configuration in the builder above, then save and refresh. Errors usually mean the payouts
            aren&apos;t evenly distributed across members.
          </p>
        )}
      </div>
    )
  }

  if (schedule.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6 text-center">
        <p className="text-gray-500">
          Schedule will be available once members join and a start date is set.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Payout Schedule</h3>
        <p className="mt-1 text-sm text-gray-500">
          Monthly rotation schedule for this box
        </p>
      </div>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Month
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Slot
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Member
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Payout Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Payout Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedSchedule.map((item, idx) => {
            const rowKey = `${item.month}-${item.slot || idx}-${item.member_id || idx}`
            return (
            <tr key={rowKey}>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">Month {item.month}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-700">#{item.slot || 1}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{item.member_name}</div>
                <div className="text-sm text-gray-500">{item.member_email}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{formatMoney(item.payout_amount)}</div>
                {item.member_expected_payout_total !== undefined && (
                  <div className="text-xs text-gray-500">
                    Expected total: {formatMoney(item.member_expected_payout_total)}
                  </div>
                )}
                {item.member_desired_payouts && item.member_desired_payouts > 1 && item.payout_number && (
                  <div className="text-xs text-primary-600 font-medium mt-1">
                    Payout {item.payout_number} of {item.member_desired_payouts}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {editingDate === `${item.month}-${item.slot}` && isOrganizer ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="date"
                      value={dateValue}
                      onChange={(e) => setDateValue(e.target.value)}
                      className="block w-40 rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors disabled:opacity-50 disabled:bg-gray-50"
                      disabled={updating}
                    />
                    <button
                      onClick={async () => {
                        if (!dateValue) return
                        setUpdating(true)
                        try {
                          // Convert date to ISO string format
                          const date = new Date(dateValue + 'T00:00:00')
                          const newDateISO = date.toISOString()
                          
                          // Save the date update
                      await boxApi.updatePayoutDate(boxId, {
                        month: item.month,
                        slot: item.slot,
                        payout_date: newDateISO,
                      })
                      const updatedData = await boxApi.getSchedule(boxId)
                      if (Array.isArray(updatedData)) {
                        setSchedule(updatedData)
                      }
                          
                          setEditingDate(null)
                          setDateValue('')
                          setError(null)
                        } catch (err) {
                          setError(err.message)
                        } finally {
                          setUpdating(false)
                        }
                      }}
                      disabled={updating || !dateValue}
                      className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Save date"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingDate(null)
                        setDateValue('')
                      }}
                      disabled={updating}
                      className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 disabled:opacity-50 transition-colors"
                      title="Cancel"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <div className="text-sm text-gray-500">
                      {new Date(item.payout_date).toLocaleDateString()}
                    </div>
                    {isOrganizer && (
                      <button
                        onClick={() => {
                          const date = new Date(item.payout_date)
                          setDateValue(date.toISOString().split('T')[0])
                          setEditingDate(`${item.month}-${item.slot}`)
                        }}
                        className="inline-flex items-center justify-center h-6 w-6 rounded text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                        title="Edit payout date"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    item.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {item.status}
                </span>
              </td>
            </tr>
            )
          })}
        </tbody>
      </table>

    </div>
  )
}
